/** Course grid card — compact course preview with lesson count and category badge. */
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import type { Course } from '@formency/shared';
import { Tag } from '@/interfaces/learner/ui';
import { LessonPages } from '@/shared/graphics';

const EASE = [0.16, 1, 0.3, 1] as const;

interface CourseCardProps {
  course: Course;
  index: number;
}

/** Derive a muted accent background from a course's accentColour, or fall back to brand violet. */
function accentGradient(colour: string | null | undefined): string {
  if (colour) {
    return `linear-gradient(135deg, ${colour}18 0%, ${colour}08 100%)`;
  }
  return 'linear-gradient(135deg, rgba(155, 107, 218, 0.06) 0%, rgba(138, 82, 200, 0.02) 100%)';
}

function accentBorder(colour: string | null | undefined): string {
  if (colour) return `${colour}20`;
  return 'var(--color-border)';
}

export default function CourseCard({ course, index }: CourseCardProps) {
  const categories = course.categories ?? [];
  const tags = course.tags ?? [];

  // Count lessons across the nested structure
  const modules = (course as Record<string, unknown>).modules as
    | Array<{ topics?: Array<{ lessons?: Array<{ isLive?: boolean }> }> }>
    | undefined;
  const lessonCount = modules
    ? modules.reduce(
        (total, mod) =>
          total +
          (mod.topics ?? []).reduce(
            (topicTotal, topic) =>
              topicTotal + (topic.lessons ?? []).filter((l) => l.isLive !== false).length,
            0
          ),
        0
      )
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: 0.1 + index * 0.06, ease: EASE }}
    >
      <Link
        to={`/courses/${course.id}`}
        className="group flex flex-col overflow-hidden rounded-2xl transition-shadow duration-300"
        style={{
          background: 'var(--color-surface-solid)',
          border: `1px solid ${accentBorder(course.accentColour)}`,
          boxShadow: 'var(--shadow-sm)',
        }}
      >
        {/* Header image or accent strip */}
        {course.headerImageUrl ? (
          <div className="relative h-36 overflow-hidden">
            <img
              src={course.headerImageUrl}
              alt=""
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            />
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(to top, rgba(0,0,0,0.3) 0%, transparent 60%)',
              }}
            />
          </div>
        ) : (
          <div className="h-2 w-full" style={{ background: accentGradient(course.accentColour) }} />
        )}

        {/* Content */}
        <div className="flex flex-1 flex-col p-5">
          {/* Icon + title */}
          <div className="flex items-start gap-3">
            {course.iconUrl && (
              <img
                src={course.iconUrl}
                alt=""
                className="mt-0.5 h-8 w-8 shrink-0 rounded-lg object-cover"
              />
            )}
            <h3
              className="text-list-title line-clamp-2"
              style={{
                margin: 0,
              }}
            >
              {course.title}
            </h3>
          </div>

          {/* Description */}
          {course.description && (
            <p className="text-list-meta-muted mt-2 line-clamp-2" style={{ marginBottom: 0 }}>
              {course.description}
            </p>
          )}

          {/* Tags */}
          {tags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {tags.slice(0, 3).map((tag) => (
                <Tag key={tag.id}>{tag.name}</Tag>
              ))}
              {tags.length > 3 && <Tag>+{tags.length - 3}</Tag>}
            </div>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Footer: stats + category */}
          <div
            className="mt-4 flex items-center justify-between border-t pt-3"
            style={{ borderColor: 'var(--color-border)' }}
          >
            <div className="flex items-center gap-4">
              {lessonCount > 0 && (
                <span
                  className="inline-flex items-center gap-1.5 font-medium"
                  style={{
                    fontSize: 'var(--type-meta-size)',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  <LessonPages size={16} />
                  {lessonCount} {lessonCount === 1 ? 'lesson' : 'lessons'}
                </span>
              )}
            </div>

            {categories.length > 0 && (
              <Tag tone="accent" style={{ maxWidth: 120 }}>
                {categories[0].name}
              </Tag>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
